import { useOktaAuth } from '@okta/okta-react';
import type { MenuProps } from 'antd';
import { Layout, Menu } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { AgentChatProvider } from '~contexts/agent-chat';
import { AgentChatDrawer } from '../agent-chat';
import { ApplicationSelector } from '../../components/application-selector';
import {
    useApplicationSelectionStore,
    useAuthStore,
    useUserStore,
} from '../../contexts/mobx';
import { useCheckNavigation } from '../../contexts/unsaved-changes';
import ArrowLineRightIcon from '../../icons/ArrowLineRight.svg?react';
import BriefcaseIcon from '../../icons/Briefcase.svg?react';
import CalendarBlankIcon from '../../icons/CalendarBlank.svg?react';
import DiceIcon from '../../icons/Dice.svg?react';
import CarwashMenuIcon from '../../icons/CarwashMenuIcon.svg?react';
import ChartlineIcon from '../../icons/ChartLine.svg?react';
import ChatsIcon from '../../icons/Chats.svg?react';
import ClubsMenuIcon from '../../icons/ClubsMenuIcon.svg?react';
import ConfigurationMenuIcon from '../../icons/ConfigurationMenuIcon.svg?react';
import RoadToRewardsMenuIcon from '../../icons/RoadToRewardsMenuIcon.svg?react';
import FishIcon from '../../icons/Fish.svg?react';
import FoldersIcon from '../../icons/Folders.svg?react';
import GridIcon from '../../icons/Grid.svg?react';
import MapPinIcon from '../../icons/MapPin.svg?react';
import MobileIcon from '../../icons/Mobile.svg?react';
import PaymentIcon from '../../icons/Payment.svg?react';
import PlugChargingIcon from '../../icons/PlugCharging.svg?react';
import ReceiptIcon from '../../icons/Receipt.svg?react';
import ReorderingIcon from '../../icons/Reordering.svg?react';
import RocketIcon from '../../icons/Rocket.svg?react';
import ScissorsIcon from '../../icons/Scissors.svg?react';
import ShapesIcon from '../../icons/Shapes.svg?react';
import SlidersIcon from '../../icons/SlidersHorizontal.svg?react';
import StarIcon from '../../icons/Star.svg?react';
import StorefrontIcon from '../../icons/Storefront.svg?react';
import StrategyIcon from '../../icons/Strategy.svg?react';
import TicketIcon from '../../icons/Ticket.svg?react';
import TreeEvergreenIcon from '../../icons/TreeEvergreen.svg?react';
import TrophyIcon from '../../icons/Trophy.svg?react';
import TwoUsersIcon from '../../icons/TwoUsers.svg?react';
import UserIcon from '../../icons/User.svg?react';
import UserGroup from '../../icons/UserGroup.svg?react';

import { ApplicationSelection } from '../../types/app-selection';
import { Text } from '../../utils/text';
import { useNavigation } from '../../utils/use-navigation';
import type { PreserveScrollState } from '../../utils/use-search-params';
import { CarWashPage } from '../car-wash-page';
import {
    AcvtPage,
    AnalyticsOverviewPage,
    CareerPage,
    CareersPage,
    CarwashAnalyticsPage,
    CategoriesPage,
    CategoryPage,
    ClientsPage,
    LoyaltyBannerPage,
    LoyaltyBannersPage,
    LoyaltyClubPage,
    LoyaltyClubsPage,
    RoadToRewardsConfigurationPage,
    RoadToRewardsAnalyticsPage,
    ComponentsShowcasePage,
    ContestPage,
    ContestsPage,
    CouponPage,
    CouponsCommonPage,
    EventPage,
    EventsPage,
    FeaturedPage,
    FeaturedReorderingPage,
    FeaturedSinglePage,
    FeedbacksPage,
    GameExternalPage,
    GamesPage,
    GeneralConfigsPage,
    LabattGamePage,
    LandingPage,
    NhlCoinsAnalyticsPage,
    NhlGamePage,
    NhlGamesDashboardPage,
    NhlGamesLeaderboardPage,
    NhlGamesPage,
    OfferPage,
    OffersPage,
    OnboardingPage,
    ProfilePage,
    PromotionGrantsPage,
    Doc31Page,
    Doc31DashboardPage,
    RechargeImagePage,
    RechargeImagesPage,
    RppAdPage,
    RppAdsPage,
    RppDashboardPage,
    RppPrizeCouponPage,
    RppPrizeCouponsPage,
    StoresDiffPage,
    StoresPage,
    TaxiDriverPage,
    UsersAnalyticsPage,
} from '../index';
import { isTaxiDriverNavVisible } from '~containers/analytics/taxi-driver-page/cards';
import {
    LEGACY_ANALYTICS_ROUTES,
    buildLegacyAnalyticsRedirect,
} from './legacy-analytics-redirect';
import style from './style.module.scss';

const { Header, Content, Sider } = Layout;

/**
 * Route-prefix -> sidebar menu key, so the highlighted nav item can be recovered
 * from the URL on a hard refresh (the selected key is otherwise click-only state,
 * which resets to its default on reload). No entry is a path-segment ancestor of
 * another, so a boundary match (exact, or prefix followed by `/`) is unambiguous.
 * More specific hyphenated routes (e.g. `/nhl-games-dashboard`) are listed before
 * their shorter siblings for readability; the boundary match does not rely on it.
 * Singular detail routes (e.g. `/coupon/edit/5`) map to their list group's key so
 * a hard refresh on a detail page keeps the owning group highlighted; the plural
 * list route (`/coupons`) is a distinct boundary, so order between the two is free.
 */
const MENU_KEY_BY_PATH: [string, string][] = [
    ['/analytics/overview', 'analytics-overview'],
    ['/analytics/users', 'users-analytics'],
    ['/analytics/carwash', 'carwash-analytics'],
    ['/analytics/taxi', 'taxi-analytics'],
    ['/onboarding', 'onboarding'],
    ['/nhl-coins/analytics', 'nhl-coins-analytics'],
    ['/nhl-games-dashboard', 'nhl-games-dashboard'],
    ['/nhl-games-leaderboard', 'nhl-games-leaderboard'],
    ['/nhl-games', 'nhl-games'],
    ['/clients', 'clients'],
    ['/feedbacks', 'feedbacks'],
    ['/categories', 'categories'],
    ['/category', 'categories'],
    ['/general-configs', 'general-configs'],
    ['/coupons', 'coupons'],
    ['/coupon', 'coupons'],
    ['/in-store-promotions', 'in-store-promotions'],
    ['/in-store-promotion', 'in-store-promotions'],
    ['/offers', 'offers'],
    ['/offer', 'offers'],
    ['/featured-reordering', 'featured-reordering'],
    ['/featured', 'featured'],
    ['/stores', 'stores'],
    ['/contests', 'contests'],
    ['/contest', 'contests'],
    ['/game-external', 'games'],
    ['/games', 'games'],
    ['/profile', 'profile'],
    ['/app-preview', 'app-preview'],
    ['/careers', 'careers'],
    ['/career', 'careers'],
    ['/recharge', 'recharge-images'],
    ['/car-wash', 'car-wash'],
    ['/events', 'events'],
    ['/event', 'events'],
    ['/road-to-rewards/analytics', 'road-to-rewards-analytics'],
    ['/road-to-rewards/configuration', 'road-to-rewards-configuration'],
    ['/loyalty-clubs', 'loyalty-clubs'],
    ['/loyalty-club', 'loyalty-clubs'],
    ['/loyalty-banners', 'loyalty-banners'],
    ['/loyalty-banner', 'loyalty-banners'],
    ['/labatt-games-dashboard', 'labatt-games-dashboard'],
    ['/rpp-dashboard', 'rpp-dashboard'],
    ['/rpp-ads', 'rpp-ads'],
    ['/rpp-prize-coupons', 'rpp-prize-coupons'],
    ['/31doc', 'doc31'],
    ['/31doc-dashboard', 'doc31-dashboard'],
];

function resolveMenuKeyFromPath(pathname: string): string | undefined {
    const match = MENU_KEY_BY_PATH.find(
        ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    );
    return match?.[1];
}

type MenuNodeLike = { key?: React.Key | null; children?: MenuProps['items'] };

/**
 * Ancestor submenu keys leading to `targetKey` in the menu tree (excluding the
 * target itself), or null if not found. Used to expand the correct group on a
 * hard refresh, including nested submenus (e.g. Molson / Loyalty).
 */
function findMenuKeyPath(
    items: MenuProps['items'],
    targetKey: string
): string[] | null {
    if (!items) return null;
    for (const item of items) {
        if (!item) continue;
        const node = item as MenuNodeLike;
        if (node.key != null && String(node.key) === targetKey) return [];
        const sub = findMenuKeyPath(node.children, targetKey);
        if (sub) return node.key != null ? [String(node.key), ...sub] : sub;
    }
    return null;
}

export const HomePage = observer(() => {
    const { oktaAuth } = useOktaAuth();
    const userStore = useUserStore();
    const location = useLocation<PreserveScrollState | undefined>();
    const navigation = useNavigation();
    const authStore = useAuthStore();
    const {
        checkNavigation,
        selectedMenuKey,
        setSelectedMenuKey,
    } = useCheckNavigation();

    const applicationSelectionStore = useApplicationSelectionStore();
    const {
        isCashier,
        hasManagementAccess,
        isCustomerServiceAgent,
    } = authStore;

    React.useEffect(() => {
        if (location.state?.preserveScroll) return;
        window.scrollTo(0, 0);
    }, [location]);

    // Keep the sidebar highlight in sync with the URL so it survives a hard
    // refresh (the selected key is otherwise click-only state that resets to its
    // default). Unmapped routes leave the current selection untouched.
    React.useEffect(() => {
        const key = resolveMenuKeyFromPath(location.pathname);
        if (key) setSelectedMenuKey(key);
    }, [location.pathname, setSelectedMenuKey]);

    const onItemSelected: MenuProps['onClick'] = ({ key }) => {
        const menuKey = String(key);

        if (menuKey === 'log-out') {
            const basename = import.meta.env.BASE_URL || '';
            const postLogoutRedirectUri = `${window.location.origin}${basename}login`;
            oktaAuth.signOut({
                postLogoutRedirectUri,
            });
            return;
        }

        const navigationMap: Record<string, () => void> = {
            'analytics-overview': navigation.goToAnalyticsOverview,
            'users-analytics': navigation.goToUsersAnalytics,
            'carwash-analytics': navigation.goToCarwashAnalytics,
            'taxi-analytics': navigation.goToTaxiAnalytics,
            onboarding: navigation.goToOnboarding,
            'nhl-coins-analytics': navigation.goToNhlCoinsAnalytics,
            'nhl-games': navigation.goToNhlGames,
            'nhl-games-dashboard': navigation.goToNhlGamesDashboard,
            'nhl-games-leaderboard': navigation.goToNhlGamesLeaderboard,
            clients: navigation.goToClients,
            feedbacks: navigation.goToFeedbacks,
            'general-configs': navigation.goToConfig,
            coupons: navigation.goToCoupons,
            'in-store-promotions': navigation.goToCouponsInStore,
            categories: navigation.goToCategories,
            offers: navigation.goToOffers,
            'loyalty-clubs': navigation.goToLoyaltyClubs,
            'road-to-rewards-analytics': navigation.goToRoadToRewardsAnalytics,
            'road-to-rewards-configuration':
                navigation.goToRoadToRewardsConfiguration,
            'loyalty-banners': navigation.goToLoyaltyBanners,
            featured: navigation.goToFeaturedList,
            'featured-reordering': navigation.goToFeaturedReordering,
            'app-preview': navigation.goToAppPreview,
            stores: navigation.goToStores,
            games: navigation.goToGames,
            contests: navigation.goToContests,
            profile: navigation.goToProfile,
            careers: navigation.goToCareers,
            'recharge-images': navigation.goToRechargesImages,
            'car-wash': navigation.goToCarWash,
            events: navigation.goToEvents,
            'labatt-games-dashboard': navigation.goToLabattGamesDashboard,
            'rpp-dashboard': navigation.goToRppDashboard,
            'rpp-ads': navigation.goToRppAds,
            'rpp-prize-coupons': navigation.goToRppPrizeCoupons,
            doc31: navigation.goToDoc31,
            'doc31-dashboard': navigation.goToDoc31Dashboard,
        };

        setSelectedMenuKey(menuKey);
        checkNavigation(
            // eslint-disable-next-line security/detect-object-injection
            navigationMap[menuKey] ?? navigation.goToHome
        );
    };

    const menuItems = React.useMemo<MenuProps['items']>(() => {
        const regularItems: NonNullable<MenuProps['items']> = [];

        if (hasManagementAccess || isCustomerServiceAgent) {
            const childAnalytics = [
                {
                    key: 'analytics-overview',
                    label: 'Overview',
                    icon: <GridIcon />,
                },
                { key: 'users-analytics', label: 'Users', icon: <UserGroup /> },
                hasManagementAccess
                    ? {
                          key: 'onboarding',
                          label: 'Onboarding',
                          icon: <RocketIcon />,
                      }
                    : null,
                { key: 'feedbacks', label: 'Feedback', icon: <ChatsIcon /> },
                // Throwaway Taxi driver campaign dashboard: Couche-Tard-only.
                isTaxiDriverNavVisible(
                    applicationSelectionStore.selectedApplication
                )
                    ? {
                          key: 'taxi-analytics',
                          label: 'Taxi driver',
                          icon: <TicketIcon />,
                      }
                    : null,
            ].filter(Boolean) as NonNullable<MenuProps['items']>;

            regularItems.push({
                key: 'analytics',
                icon: <ChartlineIcon />,
                label: 'Analytics',
                children: childAnalytics,
            });
        }

        if (hasManagementAccess) {
            const isCircleK =
                applicationSelectionStore.selectedApplication ===
                ApplicationSelection.CIRCLE_K;

            const isAllRegions =
                applicationSelectionStore.selectedApplication ===
                ApplicationSelection.ALL_REGIONS;

            const isCoucheTard =
                applicationSelectionStore.selectedApplication ===
                ApplicationSelection.COUCHE_TARD;

            const appPreviewItems = [
                {
                    key: 'app-preview',
                    label: 'App preview',
                    icon: <StorefrontIcon />,
                },
                ...(!isAllRegions
                    ? [
                          {
                              key: 'featured',
                              label: 'Featured',
                              icon: <StarIcon />,
                          },
                          {
                              key: 'featured-reordering',
                              label: 'Reordering',
                              icon: <ReorderingIcon />,
                          },
                      ]
                    : []),
            ];

            regularItems.push(
                {
                    key: 'app-preview-group',
                    icon: <MobileIcon />,
                    label: 'App preview',
                    children: appPreviewItems,
                },
                {
                    key: 'promotions-group',
                    icon: <ReceiptIcon />,
                    label: 'Promotions',
                    children: [
                        {
                            key: 'coupons',
                            label: 'Coupons',
                            icon: <TicketIcon />,
                        },
                        {
                            key: 'in-store-promotions',
                            label: 'In-store promotions',
                            icon: <StorefrontIcon />,
                        },
                        {
                            key: 'categories',
                            label: 'Categories',
                            icon: <ShapesIcon />,
                        },
                        {
                            key: 'offers',
                            label: 'Offers',
                            icon: <PaymentIcon />,
                        },
                    ],
                },
                {
                    // Top-level home for loyalty campaigns, per the Figma. It
                    // holds only Road to Rewards for now; the existing club
                    // list moves in beside it as "Other" in a later ticket.
                    key: 'clubs-group',
                    icon: <ClubsMenuIcon />,
                    label: 'Clubs',
                    children: [
                        {
                            key: 'road-to-rewards',
                            label: 'Road to Rewards',
                            icon: <RoadToRewardsMenuIcon />,
                            children: [
                                {
                                    key: 'road-to-rewards-analytics',
                                    label: 'Analytics',
                                    icon: <ChartlineIcon />,
                                },
                                {
                                    key: 'road-to-rewards-configuration',
                                    label: 'Configuration',
                                    icon: <ConfigurationMenuIcon />,
                                },
                            ],
                        },
                    ],
                },
                {
                    key: 'games-contests-group',
                    icon: <TrophyIcon />,
                    label: 'Games & Contests',
                    children: [
                        {
                            key: 'games',
                            label: 'General games',
                            icon: <TrophyIcon />,
                        },
                        {
                            key: 'contests',
                            label: 'Contests',
                            icon: <TicketIcon />,
                        },
                        !isCircleK
                            ? {
                                  key: 'molson-game',
                                  label: 'Molson game',
                                  icon: <StrategyIcon />,
                                  children: [
                                      {
                                          key: 'nhl-games-dashboard',
                                          label:
                                              Text.homePage.navBar.nhlSection
                                                  .dashboard,
                                      },
                                      {
                                          key: 'nhl-games-leaderboard',
                                          label:
                                              Text.homePage.navBar.nhlSection
                                                  .leaderboard,
                                      },
                                      {
                                          key: 'nhl-games',
                                          label:
                                              Text.homePage.navBar.nhlSection
                                                  .config,
                                      },
                                  ],
                              }
                            : null,
                        !isCircleK
                            ? {
                                  key: 'labatt-games-dashboard',
                                  label: 'Labatt game',
                                  icon: <FishIcon />,
                              }
                            : null,
                    ].filter(Boolean) as NonNullable<MenuProps['items']>,
                },
                ...(!isCircleK
                    ? [
                          {
                              key: 'carwash-group',
                              icon: <CarwashMenuIcon />,
                              label: 'Carwash',
                              children: [
                                  {
                                      key: 'carwash-analytics',
                                      label: 'Carwash dashboard',
                                      icon: <ChartlineIcon />,
                                  },
                                  {
                                      key: 'car-wash',
                                      label: 'Locations',
                                      icon: <MapPinIcon />,
                                  },
                              ],
                          },
                      ]
                    : []),
                {
                    key: 'other-sections-group',
                    icon: <GridIcon />,
                    label: 'Other sections',
                    children: [
                        {
                            key: 'events',
                            label: 'Events',
                            icon: <CalendarBlankIcon />,
                        },
                        {
                            key: 'loyalty-group',
                            icon: <StarIcon />,
                            label: 'Loyalty',
                            children: [
                                {
                                    key: 'loyalty-clubs',
                                    label: 'Clubs',
                                    icon: <TrophyIcon />,
                                },
                                isCoucheTard
                                    ? {
                                          key: 'loyalty-banners',
                                          label: 'Banners',
                                          icon: <StarIcon />,
                                      }
                                    : null,
                            ],
                        },
                        !isCircleK
                            ? {
                                  key: 'careers',
                                  label: 'Careers',
                                  icon: <BriefcaseIcon />,
                              }
                            : null,
                        !isCircleK
                            ? {
                                  key: 'recharge-images',
                                  label: 'Recharge',
                                  icon: <PlugChargingIcon />,
                              }
                            : null,
                    ].filter(Boolean) as NonNullable<MenuProps['items']>,
                }
            );
        }

        const dataItems = [
            hasManagementAccess || isCustomerServiceAgent
                ? {
                      key: 'clients',
                      label: Text.homePage.navBar.clients,
                      icon: <TwoUsersIcon />,
                  }
                : null,
            hasManagementAccess
                ? { key: 'stores', label: 'Stores', icon: <MapPinIcon /> }
                : null,
        ].filter(Boolean) as NonNullable<MenuProps['items']>;

        if (dataItems.length > 0) {
            regularItems.push({
                key: 'data-group',
                icon: <FoldersIcon />,
                label: 'Data',
                children: dataItems,
            });
        }

        regularItems.push({
            key: 'minigames-group',
            icon: <DiceIcon />,
            label: 'Minigames',
            children: [
                {
                    key: 'rpp-group',
                    icon: <ScissorsIcon />,
                    label: 'RPP',
                    children: [
                        {
                            key: 'rpp-dashboard',
                            icon: <ChartlineIcon />,
                            label: 'Dashboard',
                        },
                        {
                            key: 'rpp-ads',
                            icon: <RocketIcon />,
                            label: 'Ads',
                        },
                        {
                            key: 'rpp-prize-coupons',
                            icon: <TicketIcon />,
                            label: 'Prize Coupons',
                        },
                    ],
                },
                hasManagementAccess
                    ? {
                          key: 'nhl-coins',
                          label: 'NHL Coins',
                          icon: <TrophyIcon />,
                          children: [
                              {
                                  key: 'nhl-coins-analytics',
                                  label: 'Analytics',
                                  icon: <ChartlineIcon />,
                              },
                          ],
                      }
                    : null,
                {
                    key: 'doc31-group',
                    icon: <TreeEvergreenIcon />,
                    label: '31 Days of Circle K',
                    children: [
                        // Same access as the RPP dashboard (DOC31-99).
                        {
                            key: 'doc31-dashboard',
                            icon: <ChartlineIcon />,
                            label: 'Dashboard',
                        },
                        // Edition setup and live controls; the API admits
                        // Admin only.
                        hasManagementAccess
                            ? {
                                  key: 'doc31',
                                  icon: <SlidersIcon />,
                                  label: 'Setup',
                              }
                            : null,
                    ].filter(Boolean) as NonNullable<MenuProps['items']>,
                },
            ].filter(Boolean) as NonNullable<MenuProps['items']>,
        });

        return [
            ...regularItems,
            {
                key: 'settings-group',
                icon: <SlidersIcon />,
                label: 'Settings',
                children: [
                    { key: 'profile', label: 'Profile', icon: <UserIcon /> },
                    hasManagementAccess
                        ? {
                              key: 'general-configs',
                              label: 'Configuration',
                              icon: <SlidersIcon />,
                          }
                        : null,
                ].filter(Boolean) as MenuProps['items'],
            },
            {
                key: 'log-out',
                icon: <ArrowLineRightIcon />,
                label: 'Log out',
            },
        ];
    }, [
        hasManagementAccess,
        isCustomerServiceAgent,
        applicationSelectionStore.selectedApplication,
        applicationSelectionStore.appTarget,
    ]);

    // Expand the submenu group that owns the current route, so a hard refresh
    // opens the right group (not the hardcoded Analytics default). Computed at
    // render (not from the click-only selected key) so it is correct on mount.
    const routeMenuKey = resolveMenuKeyFromPath(location.pathname);
    const openKeysForRoute = routeMenuKey
        ? findMenuKeyPath(menuItems, routeMenuKey)
        : null;

    return (
        <AgentChatProvider>
            <div className={style.home_container}>
                <Layout>
                    <Sider
                        width={240}
                        breakpoint={isCashier ? undefined : 'lg'}
                        collapsedWidth="0"
                        className={style.sider}
                        onBreakpoint={(broken) => {
                            console.log(broken);
                        }}
                        onCollapse={(collapsed, type) => {
                            console.log(collapsed, type);
                        }}
                        collapsible={isCashier}
                        defaultCollapsed={isCashier}
                        zeroWidthTriggerStyle={{
                            background: 'lightgrey',
                            color: 'black',
                            opacity: '0.5',
                        }}
                    >
                        <div className={style.app_selector_container}>
                            <ApplicationSelector />
                        </div>
                        <Menu
                            onClick={onItemSelected}
                            theme="light"
                            mode="inline"
                            items={menuItems}
                            defaultOpenKeys={openKeysForRoute ?? ['analytics']}
                            selectedKeys={[selectedMenuKey]}
                        />
                    </Sider>
                    <Layout
                        className={
                            isCashier ? style.cashier_layout : style.layout
                        }
                    >
                        <Header className={style.header}>
                            Welcome {userStore.user?.firstName}
                        </Header>
                        <Content className={style.content}>
                            <Switch>
                                <Route path="/analytics/users">
                                    <UsersAnalyticsPage />
                                </Route>
                                <Route path="/analytics/carwash">
                                    <CarwashAnalyticsPage />
                                </Route>
                                <Route path="/analytics/overview">
                                    <AnalyticsOverviewPage />
                                </Route>
                                {LEGACY_ANALYTICS_ROUTES.map(([path]) => (
                                    <Route key={path} path={path}>
                                        <Redirect
                                            to={buildLegacyAnalyticsRedirect(
                                                path,
                                                location.search
                                            )}
                                        />
                                    </Route>
                                ))}
                                <Route path="/analytics/taxi">
                                    <TaxiDriverPage />
                                </Route>
                                <Route path="/onboarding">
                                    <OnboardingPage />
                                </Route>
                                <Route path="/nhl-coins/analytics">
                                    <NhlCoinsAnalyticsPage />
                                </Route>
                                <Route path="/nhl-games/:action/:id?">
                                    <NhlGamePage />
                                </Route>
                                <Route path="/nhl-games">
                                    <NhlGamesPage />
                                </Route>
                                <Route path="/nhl-games-dashboard">
                                    <NhlGamesDashboardPage />
                                </Route>
                                <Route path="/nhl-games-leaderboard">
                                    <NhlGamesLeaderboardPage />
                                </Route>
                                <Route path="/clients">
                                    <ClientsPage />
                                </Route>
                                <Route path="/feedbacks">
                                    <FeedbacksPage />
                                </Route>
                                <Route path="/categories">
                                    <CategoriesPage />
                                </Route>
                                <Route path="/category/:action/:id?">
                                    <CategoryPage />
                                </Route>
                                <Route path="/general-configs">
                                    <GeneralConfigsPage />
                                </Route>
                                <Route path="/coupons">
                                    <CouponsCommonPage promotionType="coupon" />
                                </Route>
                                <Route path="/in-store-promotions">
                                    <CouponsCommonPage promotionType="in_store" />
                                </Route>
                                <Route path="/coupon/:id/grants">
                                    <PromotionGrantsPage />
                                </Route>
                                <Route path="/coupon/:action/:id?">
                                    <CouponPage promotionType="coupon" />
                                </Route>
                                <Route path="/in-store-promotion/:action/:id?">
                                    <CouponPage promotionType="in_store" />
                                </Route>
                                <Route path="/offers">
                                    <OffersPage />
                                </Route>
                                <Route path="/offer/:action/:id?">
                                    <OfferPage />
                                </Route>
                                <Route path="/featured/:action/:id?">
                                    <FeaturedSinglePage />
                                </Route>
                                <Route path="/featured">
                                    <FeaturedPage />
                                </Route>
                                <Route path="/featured-reordering">
                                    <FeaturedReorderingPage />
                                </Route>
                                <Route path="/stores">
                                    <StoresPage />
                                </Route>
                                <Route path="/stores-diff">
                                    <StoresDiffPage />
                                </Route>
                                <Route path="/games">
                                    <GamesPage />
                                </Route>
                                <Route path="/contests">
                                    <ContestsPage />
                                </Route>
                                <Route path="/contest/:action/:id?/:tab?">
                                    <ContestPage />
                                </Route>
                                <Route path="/game-external/:action/:id?">
                                    <GameExternalPage />
                                </Route>
                                <Route path="/game-external/:action">
                                    <GameExternalPage />
                                </Route>
                                <Route path="/profile">
                                    <ProfilePage />
                                </Route>
                                <Route path="/app-preview">
                                    <AcvtPage />
                                </Route>
                                <Route path="/careers">
                                    <CareersPage />
                                </Route>
                                <Route path="/career/:action/:id?">
                                    <CareerPage />
                                </Route>
                                <Route path="/recharge/images/:action/:id?">
                                    <RechargeImagePage />
                                </Route>
                                <Route path="/recharge">
                                    <RechargeImagesPage />
                                </Route>
                                <Route path="/car-wash">
                                    <CarWashPage />
                                </Route>
                                <Route path="/events">
                                    <EventsPage />
                                </Route>
                                <Route path="/event/:action/:id?">
                                    <EventPage />
                                </Route>
                                <Route path="/road-to-rewards/analytics">
                                    <RoadToRewardsAnalyticsPage />
                                </Route>
                                <Route path="/road-to-rewards/configuration">
                                    <RoadToRewardsConfigurationPage />
                                </Route>
                                <Route path="/loyalty-clubs">
                                    <LoyaltyClubsPage />
                                </Route>
                                <Route path="/loyalty-club/:action/:id?">
                                    <LoyaltyClubPage />
                                </Route>
                                <Route path="/loyalty-banners">
                                    <LoyaltyBannersPage />
                                </Route>
                                <Route path="/loyalty-banner/:action/:id?">
                                    <LoyaltyBannerPage />
                                </Route>
                                <Route path="/labatt-games-dashboard">
                                    <LabattGamePage />
                                </Route>
                                <Route path="/rpp-dashboard">
                                    <RppDashboardPage />
                                </Route>
                                <Route path="/rpp-ads/:action/:id?">
                                    <RppAdPage />
                                </Route>
                                <Route path="/rpp-ads">
                                    <RppAdsPage />
                                </Route>
                                <Route path="/rpp-prize-coupons/:action/:id?">
                                    <RppPrizeCouponPage />
                                </Route>
                                <Route path="/rpp-prize-coupons">
                                    <RppPrizeCouponsPage />
                                </Route>
                                <Route path="/31doc-dashboard">
                                    <Doc31DashboardPage />
                                </Route>
                                <Route path="/31doc">
                                    <Doc31Page />
                                </Route>
                                {import.meta.env.MODE !== 'production' && (
                                    <Route path="/components-showcase">
                                        <ComponentsShowcasePage />
                                    </Route>
                                )}
                                <Route path="/">
                                    <LandingPage />
                                </Route>
                            </Switch>
                        </Content>
                    </Layout>
                </Layout>
            </div>
            <AgentChatDrawer />
        </AgentChatProvider>
    );
});
